// The Activity tab's numbers: the last-7-days balance (the same targets What's
// next plans from), how often you aim to train, weeks on target, and the
// history grouped into calendar weeks. Pure, so it can be tested without the
// contexts.

import { summariseTrainingHistory, sessionHappened } from "./trainingHistory";
import {
  WEEKLY_TARGETS,
  adaptationsForWorkoutType,
  readCountsAs,
  summariseWeeklyBalance,
} from "./trainingBalance";
import {
  MAJOR_MUSCLE_GROUPS,
  WEEKLY_SET_CEILING,
  WEEKLY_SET_TARGET,
  describeUndertrained,
  weeklyMuscleSets,
} from "./muscleVolume";
import { zone2Minutes } from "./heartRateZones";

const DAY_MS = 24 * 60 * 60 * 1000;
export const DEFAULT_WEEKLY_SESSIONS = 3;

/**
 * How many days a week the user is aiming for, and where that comes from:
 * an active programme's schedule first, then the Profile, then 3.
 */
export const resolveWeeklyTarget = ({ programme, userProfile } = {}) => {
  const fromProgramme = Number(programme?.days_per_week);
  if (Number.isInteger(fromProgramme) && fromProgramme > 0) {
    return { days: fromProgramme, source: "programme" };
  }
  const fromProfile = Number(userProfile?.weeklySessions);
  if (Number.isInteger(fromProfile) && fromProfile > 0) {
    return { days: fromProfile, source: "profile" };
  }
  return { days: DEFAULT_WEEKLY_SESSIONS, source: "default" };
};

/** Midnight on the Monday of the week containing `date` (local time). */
export const weekStart = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const offset = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - offset);
  return d;
};

// "YYYY-MM-DD" day keys are local days; new Date() would read them as UTC
// midnight and slip a day west of Greenwich.
const toDate = (value) => {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(value);
};

const dayKey = (date) => {
  const d = toDate(date);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

const month = (d) => d.toLocaleDateString("en-AU", { month: "short" });

/** "28 Sept – 4 Oct", or "14 – 20 Sept" within one month. */
export const formatRange = (start) => {
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return start.getMonth() === end.getMonth()
    ? `${start.getDate()} – ${end.getDate()} ${month(end)}`
    : `${start.getDate()} ${month(start)} – ${end.getDate()} ${month(end)}`;
};

/**
 * Sessions grouped into calendar weeks (Monday to Sunday), newest first,
 * keeping each week's sessions in the order given.
 */
export const groupSessionsByWeek = (sessions, { now = Date.now() } = {}) => {
  const thisWeek = weekStart(now).getTime();
  const groups = new Map();
  for (const session of Array.isArray(sessions) ? sessions : []) {
    const when = new Date(session?.started_at);
    if (Number.isNaN(when.getTime())) continue;
    const start = weekStart(when);
    const key = start.getTime();
    if (!groups.has(key)) {
      const weeksAgo = Math.round((thisWeek - key) / (7 * DAY_MS));
      groups.set(key, {
        key,
        start,
        title:
          weeksAgo <= 0
            ? "This week"
            : weeksAgo === 1
              ? "Last week"
              : `Week of ${start.getDate()} ${month(start)}`,
        range: formatRange(start),
        sessions: [],
        minutes: 0,
      });
    }
    const group = groups.get(key);
    group.sessions.push(session);
    const mins = Number(session.duration_minutes);
    if (Number.isFinite(mins) && mins > 0) group.minutes += mins;
  }
  return [...groups.values()].sort((a, b) => b.key - a.key);
};

/**
 * Weeks in a row on target: calendar weeks with at least `target` training
 * days, counting back from last week. This week joins the run once it's met,
 * but an unfinished week never breaks it. Rest days never matter.
 *
 * @param {Array<string|Date>} dates  Every training day (any order, repeats ok)
 */
export const weeksOnTarget = (dates, target, { now = Date.now() } = {}) => {
  const daysPerWeek = new Map();
  const seen = new Set();
  for (const date of Array.isArray(dates) ? dates : []) {
    const d = toDate(date);
    if (Number.isNaN(d.getTime())) continue;
    const day = dayKey(d);
    if (seen.has(day)) continue;
    seen.add(day);
    const key = weekStart(d).getTime();
    daysPerWeek.set(key, (daysPerWeek.get(key) || 0) + 1);
  }
  const met = (start) => (daysPerWeek.get(start.getTime()) || 0) >= target;

  const cursor = weekStart(now);
  let run = met(cursor) ? 1 : 0;
  cursor.setDate(cursor.getDate() - 7);
  while (met(cursor)) {
    run += 1;
    cursor.setDate(cursor.getDate() - 7);
  }
  return run;
};

/** Training days so far in the current calendar week. */
export const daysThisWeek = (dates, { now = Date.now() } = {}) => {
  const start = weekStart(now).getTime();
  const days = new Set();
  for (const date of Array.isArray(dates) ? dates : []) {
    const d = toDate(date);
    if (!Number.isNaN(d.getTime()) && weekStart(d).getTime() === start) {
      days.add(dayKey(d));
    }
  }
  return days.size;
};

/**
 * Training days in the last 7 days (today and the 6 before), the same rolling
 * window as the target rows above it, so the card's numbers agree.
 */
export const daysInLastSeven = (dates, { now = Date.now() } = {}) => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const from = new Date(today);
  from.setDate(from.getDate() - 6);
  const days = new Set();
  for (const date of Array.isArray(dates) ? dates : []) {
    const d = toDate(date);
    if (Number.isNaN(d.getTime()) || d.getTime() < from.getTime() || d.getTime() > now) continue;
    days.add(dayKey(d));
  }
  return days.size;
};

/**
 * What a session mostly trained, for its icon and colour: strength, hard
 * intervals, cardio, or other. Read loosely from its type and plan name, the
 * same way the weekly balance reads it.
 */
export const sessionKind = (session) => {
  const tags =
    readCountsAs(session?.plan_data?.countsAs) ??
    adaptationsForWorkoutType(
      `${session?.workout_type || ""} ${session?.plan_data?.label || ""}`,
    );
  if (tags.includes("resistance")) return "strength";
  if (tags.includes("anaerobic")) return "hard";
  if (tags.includes("aerobic")) return "cardio";
  return "other";
};

const BALANCE_LABELS = {
  resistance: ["Strength", "strength session", "strength sessions"],
  anaerobic: ["Hard intervals", "hard interval session", "hard interval sessions"],
  aerobic: ["Cardio", "cardio session", "cardio sessions"],
};

const NUMBER_WORDS = ["no", "one", "two", "three", "four", "five"];

/**
 * The last 7 days against the weekly targets: one row per adaptation, a
 * sentence on what's missing, and hard sets per major muscle.
 */
export const summariseLastSevenDays = (
  sessions,
  { now = Date.now(), targets = WEEKLY_TARGETS, zone2Target = 0 } = {},
) => {
  const history = summariseTrainingHistory(sessions, {
    lookbackDays: 7,
    now,
    maxSessions: 100,
  }).filter((h) => h.daysAgo < 7);
  const balance = summariseWeeklyBalance(history, targets);
  const rows = Object.entries(targets).map(([tag, target]) => ({
    tag,
    label: BALANCE_LABELS[tag][0],
    done: balance.counts[tag] || 0,
    target,
  }));
  const short = rows.filter((r) => r.done < r.target);
  const needs = short.map((r) => {
    const n = r.target - r.done;
    return `${NUMBER_WORDS[n] || n} more ${BALANCE_LABELS[r.tag][n === 1 ? 1 : 2]}`;
  });
  const joined =
    needs.length > 1
      ? `${needs.slice(0, -1).join(", ")} and ${needs[needs.length - 1]}`
      : needs[0];
  const note =
    history.length === 0
      ? null
      : short.length === 0
        ? null // the green counts already say it
        : `${joined.charAt(0).toUpperCase()}${joined.slice(1)} would put your week on target.`;

  const muscles = weeklyMuscleSets(sessions, { now });
  const anySets = MAJOR_MUSCLE_GROUPS.some((m) => (muscles[m] || 0) > 0);
  const recent = (Array.isArray(sessions) ? sessions : []).filter((s) => {
    if (!sessionHappened(s)) return false;
    const t = new Date(s.started_at).getTime();
    return Number.isFinite(t) && now - t < 7 * DAY_MS;
  });
  // Real Zone 2 time (#94) from sessions that recorded heart rate.
  const zoned = recent.filter((s) => s.telemetry_summary?.hr?.zones);
  const zone2 = zoned.length
    ? {
        minutes: zoned.reduce((sum, s) => sum + zone2Minutes(s.telemetry_summary.hr.zones), 0),
        sessions: zoned.length,
        target: zone2Target,
      }
    : null;
  return {
    zone2,
    count: recent.length,
    minutes: recent.reduce(
      (sum, s) => sum + (Number(s.duration_minutes) > 0 ? Number(s.duration_minutes) : 0),
      0,
    ),
    rows,
    note,
    muscles: anySets
      ? MAJOR_MUSCLE_GROUPS.map((m) => ({ muscle: m, sets: muscles[m] || 0 }))
      : null,
    muscleNote: anySets ? describeUndertrained(muscles) : null,
    setTarget: WEEKLY_SET_TARGET,
    setCeiling: WEEKLY_SET_CEILING,
  };
};
